import {
  useIsSerialProcess,
  useProcessesInVisibleGroups,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  DASHBOARD_CARD_PROPS,
  PNL_SUMMARY_CHART_CARD_CSS,
} from "../../dashboardPagesContentUtils";
import PnlSummaryChart from "components/Dashboard/PnlSummaryChart/PnlSummaryChart";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import { Badge, Box, Tooltip, Typography } from "@mui/material";
import styled, { css } from "styled-components/macro";
import { useMemo } from "react";
import {
  LINE_ROLE_ICONS,
  useLeaderConstraintProcessIds,
} from "components/Dashboard/Tiles/ProcessTile/LineRoleDecorator";
import {
  lightAccentSuccess10,
  lightAccentSuccess50,
  lightAccentSuccess90,
} from "constants/colors";
import {
  CUSTOM_CONSTRAINT_AND_OTHER_PROCESSES_NAME,
  OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART,
} from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import PnlChartCardControls from "components/Dashboard/PnlSummaryChart/PnlChartCardControls";
import { AUTO_DOWNLOAD_IMG } from "pages/SnapshotsPage/SnapshotElementButton";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import {
  PNL_CHART_CARD_ID_HUBLET_PAGE,
  useIsPnlChartExpandedHubletPage,
} from "pages/DashboardPages/dashboardPagesUtils";

const ProcessIndicatorContainer = styled(Box)`
  position: absolute;
  top: 8px;
  left: 36px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: scale(0.8);
  transform-origin: top left;
  .MuiBadge-badge {
    top: 10px !important;
    left: -10px !important;
    svg {
      transform: scale(0.8);
      color: ${lightAccentSuccess90};
      background: ${lightAccentSuccess10};
      border: 1px solid ${lightAccentSuccess50};
      border-radius: 4px;
    }
  }

  .process-indicator {
    display: flex;
    align-items: center;
    margin-left: 12px;
  }
`;

export function PnlChartCard() {
  const cKey = useCustomerKey();

  const isSerialProcess = useIsSerialProcess();
  const visibleGroups = useVisibleGroups();
  const { translate } = useFlightTranslation();
  // Should only be one group shown on timeline page
  const groupForTimelinePage = visibleGroups?.[0]?.summaryGroup;

  const shouldUseOverridenProcesses =
    OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART?.[cKey]?.[
      groupForTimelinePage
    ] !== undefined;
  const [
    {
      general: { enableSecondaryProcessesPnlChart },
    },
  ] = useFlightConfig();
  // Show second PnL chart for serial lines or if overridenOtherProcessesPnlChartMachines has machines
  const shouldShowOtherPnlCharts =
    isSerialProcess ||
    shouldUseOverridenProcesses ||
    enableSecondaryProcessesPnlChart;

  const customContraintAndOtherProcessesName =
    CUSTOM_CONSTRAINT_AND_OTHER_PROCESSES_NAME?.[cKey];

  // For leader/constraint indicators
  const { leaderProcessId, constraintProcessIds } =
    useLeaderConstraintProcessIds();
  // Get all processes to find names by ID
  const allProcesses = useProcessesInVisibleGroups();

  // Find leader and constraint processes
  const leaderProcess = useMemo(() => {
    return leaderProcessId
      ? allProcesses.find(p => p.id === leaderProcessId)
      : null;
  }, [leaderProcessId, allProcesses]);

  const constraintProcesses = useMemo(() => {
    if (!constraintProcessIds?.length) {
      return [];
    }
    return allProcesses.filter(p => constraintProcessIds.includes(p.id));
  }, [constraintProcessIds, allProcesses]);

  // Check if there is a leader or constraint process
  const hasLeader = Boolean(leaderProcess);
  const hasConstraint = Boolean(constraintProcesses.length);
  const pnlBreakdownTitle = translate("Performance_&_Loss_Breakdown");
  const pnlBreakdownChartTitle = `${pnlBreakdownTitle}${
    shouldShowOtherPnlCharts
      ? ` — ${
          customContraintAndOtherProcessesName?.constraint ??
          (leaderProcessId ? "Leader Process" : "Constraint Processes")
        }`
      : ""
  }`;

  const pnlBreakdownOtherProcessesChartTitle = `${pnlBreakdownTitle} — ${
    customContraintAndOtherProcessesName?.other ?? "Other Processes"
  }`;

  const [, setIsExpanded] = useIsPnlChartExpandedHubletPage();

  return (
    <>
      <CardWithControls
        className={"Card_PnlChart Card_PnlChart_TimelinePage break-before"}
        id={PNL_CHART_CARD_ID_HUBLET_PAGE}
        {...DASHBOARD_CARD_PROPS}
        title={pnlBreakdownChartTitle}
        customCss={css`
          ${PNL_SUMMARY_CHART_CARD_CSS}
          position: relative;
        `}
        CardConfigControls={<PnlChartCardControls />}
        forceExpanded={window.location.search.includes(
          `autoDownload=${AUTO_DOWNLOAD_IMG.pnl}`
        )}
        onCollapse={newIsExpanded => {
          setIsExpanded(newIsExpanded);
        }}
      >
        {/* Leader/Constraint process indicators */}
        {(hasLeader || hasConstraint) && (
          <ProcessIndicatorContainer>
            {hasLeader && leaderProcess && (
              <div className="process-indicator">
                <Tooltip title={translate("Leader_Process")} disableInteractive>
                  <Badge
                    badgeContent={LINE_ROLE_ICONS.LEADER}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                    className="machineNameContainerWithBadge"
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ fontWeight: 500, pl: 1 }}
                    >
                      {leaderProcess.name}
                    </Typography>
                  </Badge>
                </Tooltip>
              </div>
            )}
            {hasConstraint &&
              constraintProcesses.map(process => (
                <div
                  className="process-indicator"
                  key={`constraint-${process.id}`}
                >
                  <Tooltip
                    title={translate("Constraint_Process")}
                    disableInteractive
                  >
                    <Badge
                      badgeContent={LINE_ROLE_ICONS.CONSTRAINT}
                      anchorOrigin={{ horizontal: "left", vertical: "top" }}
                      className="machineNameContainerWithBadge"
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 500, pl: 1 }}
                      >
                        {process.name}
                      </Typography>
                    </Badge>
                  </Tooltip>
                </div>
              ))}
          </ProcessIndicatorContainer>
        )}
        <PnlSummaryChart />
      </CardWithControls>
      {shouldShowOtherPnlCharts ? (
        <CardWithControls
          {...DASHBOARD_CARD_PROPS}
          title={pnlBreakdownOtherProcessesChartTitle}
          customCss={css`
            ${PNL_SUMMARY_CHART_CARD_CSS}
            position: relative;
          `}
        >
          <PnlSummaryChart
            {...{
              isSubordinateProcessesOnly: true,
            }} // this chart only shows the non-serial "other processes" i.e. isSelected: false or machines from OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART
          />
        </CardWithControls>
      ) : null}
    </>
  );
}
